import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Briefcase, Plus, Search, Edit3, Trash2, EyeOff, Eye, ExternalLink, ArrowUp, ArrowDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Toaster, toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminJobsPage() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const navigate = useNavigate();

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/jobs`);
      setJobs(data || []);
    } catch {
      toast.error("Failed to load jobs.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return jobs;
    return jobs.filter(
      (j) =>
        j.slug.toLowerCase().includes(q) ||
        j.title.toLowerCase().includes(q) ||
        (j.department || "").toLowerCase().includes(q) ||
        (j.location || "").toLowerCase().includes(q)
    );
  }, [jobs, query]);

  const handleDelete = async (slug) => {
    if (!window.confirm(`Delete job "${slug}"? This can't be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/jobs/${slug}`);
      setJobs((prev) => prev.filter((j) => j.slug !== slug));
      toast.success("Job deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const handleTogglePublish = async (job) => {
    try {
      const { data } = await axios.patch(`${API}/admin/jobs/${job.slug}`, {
        published: !job.published,
      });
      setJobs((prev) => prev.map((j) => (j.slug === job.slug ? { ...j, ...data } : j)));
      toast.success(data.published ? "Published." : "Unpublished.");
    } catch {
      toast.error("Update failed.");
    }
  };

  const handleReorder = async (job, direction) => {
    // Swap `order` with the neighbour in that direction so admins can easily
    // arrange the list as shown on /careers.
    const sorted = [...jobs].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const idx = sorted.findIndex((j) => j.slug === job.slug);
    const swapWith = direction === "up" ? sorted[idx - 1] : sorted[idx + 1];
    if (!swapWith) return;
    try {
      await Promise.all([
        axios.patch(`${API}/admin/jobs/${job.slug}`, { order: swapWith.order ?? 0 }),
        axios.patch(`${API}/admin/jobs/${swapWith.slug}`, { order: job.order ?? 0 }),
      ]);
      fetchJobs();
    } catch {
      toast.error("Reorder failed.");
    }
  };

  return (
    <div data-testid="admin-jobs-page" className="bg-white min-h-full">
      <Toaster position="top-right" richColors />

      <div className="px-5 lg:px-8 py-5 border-b border-slate-200 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-slate-900">
            <Briefcase className="h-4 w-4 text-sky-600" />
            <h1 className="text-base font-bold">Jobs / Careers</h1>
            <span className="text-xs text-slate-400">({jobs.length})</span>
          </div>
          <p className="text-xs text-slate-500 mt-1">Manage open positions shown on /careers and their detail pages.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search jobs…"
              className="pl-9 h-9 w-60 text-xs"
              data-testid="admin-jobs-search"
            />
          </div>
          <Button
            onClick={() => navigate("/admin/jobs/new")}
            data-testid="admin-jobs-new"
            className="bg-sky-600 hover:bg-sky-700 text-white h-9 rounded-lg text-xs font-semibold"
          >
            <Plus className="h-3.5 w-3.5 mr-1" /> New job
          </Button>
        </div>
      </div>

      <div className="p-5 lg:p-8">
        {loading ? (
          <p className="text-sm text-slate-400 text-center py-10">Loading…</p>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-slate-200 rounded-xl bg-slate-50/50">
            <p className="text-sm text-slate-500">
              {jobs.length === 0 ? "No jobs yet. Create your first job posting." : "No jobs match your search."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto border border-slate-200 rounded-xl">
            <table className="w-full text-sm" data-testid="admin-jobs-table">
              <thead className="bg-slate-50 text-[10px] uppercase text-slate-500 tracking-widest">
                <tr>
                  <th className="text-left px-4 py-2.5 font-semibold">Order</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Title</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Department</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Location</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Type</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Status</th>
                  <th className="text-right px-4 py-2.5 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {filtered.map((j, i) => (
                  <tr key={j.slug} data-testid={`admin-jobs-row-${j.slug}`} className="hover:bg-slate-50/50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <span className="text-xs font-mono text-slate-500 w-5">{j.order ?? 0}</span>
                        <button
                          type="button"
                          onClick={() => handleReorder(j, "up")}
                          disabled={i === 0}
                          title="Move up"
                          data-testid={`admin-jobs-up-${j.slug}`}
                          className="p-1 rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <ArrowUp className="h-3 w-3" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleReorder(j, "down")}
                          disabled={i === filtered.length - 1}
                          title="Move down"
                          data-testid={`admin-jobs-down-${j.slug}`}
                          className="p-1 rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <ArrowDown className="h-3 w-3" />
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm font-semibold text-slate-900 line-clamp-1">{j.title}</p>
                      <p className="text-[10px] text-slate-400 font-mono mt-0.5">/careers/{j.slug}</p>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-600">{j.department || "-"}</td>
                    <td className="px-4 py-3 text-xs text-slate-600">{j.location || "-"}</td>
                    <td className="px-4 py-3">
                      <span className="text-[10px] font-medium text-sky-700 bg-sky-50 border border-sky-100 rounded-full px-1.5 py-0.5">
                        {j.type || "Full-time"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {j.published ? (
                        <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 inline-flex items-center gap-1">
                          <Eye className="h-2.5 w-2.5" /> Published
                        </span>
                      ) : (
                        <span className="text-[10px] font-semibold text-slate-600 bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5 inline-flex items-center gap-1">
                          <EyeOff className="h-2.5 w-2.5" /> Draft
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <Link
                          to={`/careers/${j.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="View on site"
                          className="p-1.5 rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </Link>
                        <button
                          type="button"
                          onClick={() => handleTogglePublish(j)}
                          title={j.published ? "Unpublish" : "Publish"}
                          data-testid={`admin-jobs-toggle-${j.slug}`}
                          className="p-1.5 rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                        >
                          {j.published ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                        </button>
                        <Link
                          to={`/admin/jobs/${j.slug}`}
                          data-testid={`admin-jobs-edit-${j.slug}`}
                          className="p-1.5 rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                        >
                          <Edit3 className="h-3.5 w-3.5" />
                        </Link>
                        <button
                          type="button"
                          onClick={() => handleDelete(j.slug)}
                          title="Delete"
                          data-testid={`admin-jobs-delete-${j.slug}`}
                          className="p-1.5 rounded text-slate-400 hover:bg-red-50 hover:text-red-500"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
